/**
 * Live pricing from `public.pricing_skus`.
 *
 * `src/config/pricing.ts` holds fallback constants so the very first paint can
 * render a price without waiting on the network. This hook folds the
 * authoritative database rows into that map and subscribes to Realtime, so a
 * price changed in the Pricing & Promos admin reaches every public surface
 * without a deploy.
 *
 * `PricingSync` mounts it high in the tree; page consumers share the same
 * reference-counted subscription and rendered-price version.
 */
import { useEffect, useSyncExternalStore } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  applyPricingSkus,
  applyPromoCodes,
  type PricingSkuRow,
  type PublicPromoRow,
} from "@/config/pricing";

export const PRICING_SKUS_QUERY_KEY = ["pricing-skus"] as const;
export const PUBLIC_PROMOS_QUERY_KEY = ["public-promos"] as const;

const loadPricingSkus = async (): Promise<PricingSkuRow[]> => {
  const { data, error } = await supabase
    .from("pricing_skus")
    .select("code,sku_type,pathway_slug,amount_paise,currency")
    .eq("active", true);

  if (error) throw error;
  return (data ?? []) as PricingSkuRow[];
};

const loadPublicPromos = async (): Promise<PublicPromoRow[]> => {
  const { data, error } = await supabase
    .from("public_promos")
    .select("code,discount_type,discount_value,applies_to_skus");

  if (error) throw error;
  return (data ?? []) as PublicPromoRow[];
};

let pricingVersion = 0;
let pricingChannel: ReturnType<typeof supabase.channel> | null = null;
let channelGeneration = 0;
const pricingListeners = new Set<() => void>();

const notifyPricingChanged = () => {
  pricingVersion += 1;
  pricingListeners.forEach((listener) => listener());
};
const getPricingVersion = () => pricingVersion;

// All mounted consumers share one subscription. A new generation avoids reusing
// a channel whose asynchronous removal is still pending after the last cleanup.
function subscribePricing(listener: () => void) {
  pricingListeners.add(listener);
  if (!pricingChannel) {
    const channel = supabase
      .channel(`pricing-skus-sync-${++channelGeneration}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pricing_skus" },
        () => {
          void loadPricingSkus()
            .then((rows) => {
              if (pricingChannel === channel && applyPricingSkus(rows)) notifyPricingChanged();
            })
            .catch(() => { /* retain last known pricing */ });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "promo_codes" },
        () => {
          void loadPublicPromos()
            .then((rows) => {
              if (pricingChannel === channel && applyPromoCodes(rows)) notifyPricingChanged();
            })
            .catch(() => { /* retain last known promo copy */ });
        },
      )
      .subscribe();
    pricingChannel = channel;
  }

  return () => {
    pricingListeners.delete(listener);
    if (pricingListeners.size === 0 && pricingChannel) {
      const channel = pricingChannel;
      pricingChannel = null;
      void supabase.removeChannel(channel);
    }
  };
}

/** Force a refetch after an admin edits a price or a promo code. */
export const invalidatePricingSkus = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: PRICING_SKUS_QUERY_KEY });
  return queryClient.invalidateQueries({ queryKey: PUBLIC_PROMOS_QUERY_KEY });
};

/**
 * Returns a version counter that only increments when a rendered price
 * actually changed — so the common case (database matches the fallbacks)
 * costs nothing.
 */
export function usePricingSkus() {
  const version = useSyncExternalStore(subscribePricing, getPricingVersion, getPricingVersion);

  const { data } = useQuery({
    queryKey: PRICING_SKUS_QUERY_KEY,
    queryFn: loadPricingSkus,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  const { data: promos } = useQuery({
    queryKey: PUBLIC_PROMOS_QUERY_KEY,
    queryFn: loadPublicPromos,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  useEffect(() => {
    if (!data) return;
    if (applyPricingSkus(data)) notifyPricingChanged();
  }, [data]);

  useEffect(() => {
    if (!promos) return;
    if (applyPromoCodes(promos)) notifyPricingChanged();
  }, [promos]);

  return { version };
}
